import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AssetTableItem } from "../../../types/types";
import { getTypeConfig } from "./typeConfig";
import AssetReferenceChip from "./AssetReferenceChip";

export interface AssetMarkdownFieldProps {
  /** Raw Markdown source (may contain [[asset:ID|Name]] markers). */
  value: string;
  /** Section heading shown above the field (e.g. "DESCRIPTION"). */
  label?: string;
  /** Called when user clicks Save; shows Save/Cancel buttons. Optional when onChange is provided. */
  onSave?: (_newValue: string) => Promise<void>;
  /**
   * Controlled editor mode: called on every keystroke instead of a Save button.
   * When provided the view/edit toggle and Save/Cancel buttons are hidden and the
   * field is always in edit mode.  The parent is responsible for persisting changes.
   */
  onChange?: (_newValue: string) => void;
  /** Full flat list of assets – used for autocomplete and chip lookup. */
  allAssets: AssetTableItem[];
  /** Called when a reference chip is clicked. */
  onReferenceNavigate: (_id: number) => void;
  /** If true the field is always in view-only mode with no edit toggle. */
  readOnly?: boolean;
  /** Optional: called whenever the edit mode changes (for parent components that need to react). */
  onModeChange?: (_mode: "view" | "edit") => void;
}

/** Split a Markdown string into text and [[asset:ID|Name]] reference tokens. */
function parseMarkdown(
  content: string
): Array<{ type: "text"; value: string } | { type: "ref"; id: number; name: string }> {
  const parts: Array<{ type: "text"; value: string } | { type: "ref"; id: number; name: string }> =
    [];
  const regex = /\[\[asset:(\d+)\|([^\]]+)\]\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: "ref", id: parseInt(match[1]), name: match[2] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: "text", value: content.slice(lastIndex) });
  }

  return parts;
}

/** Detect if cursor is inside an open [[ trigger (not yet closed with ]]). Returns query string or null. */
function detectAutocomplete(text: string, cursorPos: number): string | null {
  const before = text.slice(0, cursorPos);
  const triggerIdx = before.lastIndexOf("[[");
  if (triggerIdx === -1) return null;
  const afterTrigger = before.slice(triggerIdx + 2); // 2 = "[[".length
  if (afterTrigger.includes("]]")) return null; // Already closed
  return afterTrigger;
}

const AssetMarkdownField: React.FC<AssetMarkdownFieldProps> = ({
  value,
  label,
  onSave,
  onChange,
  allAssets,
  onReferenceNavigate,
  readOnly = false,
  onModeChange,
}) => {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // When onChange is provided the component acts as a controlled editor (always in edit textarea mode).
  const isControlled = !!onChange;

  // Sync draft when value changes from outside.
  // In controlled mode `mode` stays "view" so the condition is always true, which
  // lets parent resets (Cancel button) propagate correctly.
  useEffect(() => {
    if (mode === "view") setDraft(value);
  }, [value, mode]);

  // Filtered autocomplete list
  const autocompleteAssets =
    autocompleteQuery !== null
      ? allAssets
          .filter((a) => a.name.toLowerCase().includes(autocompleteQuery.toLowerCase()))
          .slice(0, 10)
      : [];

  // Auto-focus first item and recalculate dropdown position whenever query changes
  useEffect(() => {
    if (autocompleteQuery !== null) {
      setActiveIndex(0);
      const updatePos = () => {
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
        }
      };
      updatePos();
      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
      return () => {
        window.removeEventListener("scroll", updatePos, true);
        window.removeEventListener("resize", updatePos);
      };
    } else {
      setActiveIndex(-1);
      setDropdownPos(null);
    }
  }, [autocompleteQuery]);

  // Scroll active item into view on keyboard navigation
  useEffect(() => {
    if (activeIndex >= 0 && autocompleteRef.current) {
      const items = autocompleteRef.current.querySelectorAll<HTMLElement>("[role='option']");
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleEnterEdit = () => {
    setDraft(value);
    setMode("edit");
    onModeChange?.("edit");
  };

  const handleCancel = () => {
    setDraft(value);
    setMode("view");
    setAutocompleteQuery(null);
    onModeChange?.("view");
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(draft);
      setMode("view");
      onModeChange?.("view");
    } finally {
      setSaving(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setDraft(newText);
    const cursor = e.target.selectionStart ?? newText.length;
    const query = detectAutocomplete(newText, cursor);
    setAutocompleteQuery(query);
    // Notify parent immediately in controlled mode
    onChange?.(newText);
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (autocompleteQuery !== null && autocompleteAssets.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, autocompleteAssets.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        insertReference(autocompleteAssets[activeIndex]);
        return;
      }
    }
    if (e.key === "Escape") {
      setAutocompleteQuery(null);
    }
  };

  const insertReference = useCallback(
    (asset: AssetTableItem) => {
      if (!textareaRef.current) return;
      const cursor = textareaRef.current.selectionStart ?? draft.length;
      const before = draft.slice(0, cursor);
      const after = draft.slice(cursor);
      const triggerIdx = before.lastIndexOf("[[");
      if (triggerIdx === -1) return;
      const newText = before.slice(0, triggerIdx) + `[[asset:${asset.id}|${asset.name}]]` + after;
      setDraft(newText);
      onChange?.(newText);
      setAutocompleteQuery(null);
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursor = triggerIdx + `[[asset:${asset.id}|${asset.name}]]`.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursor, newCursor);
        }
      }, 0);
    },
    [draft, onChange]
  );

  // Close autocomplete on click-outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setAutocompleteQuery(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /**
   * Walk React children and replace any string containing [[asset:ID|Name]] markers
   * with a mix of plain text and AssetReferenceChip elements so that refs render
   * inline within the surrounding paragraph or list item.
   */
  const processChildrenForRefs = useCallback(
    (children: React.ReactNode): React.ReactNode[] => {
      const result: React.ReactNode[] = [];
      React.Children.forEach(children, (child, i) => {
        if (typeof child === "string") {
          const parts = parseMarkdown(child);
          if (parts.length === 1 && parts[0].type === "text") {
            result.push(child);
          } else {
            parts.forEach((part, j) => {
              if (part.type === "ref") {
                result.push(
                  <AssetReferenceChip
                    key={`${i}-${j}`}
                    assetId={part.id}
                    displayName={part.name}
                    allAssets={allAssets}
                    onNavigate={onReferenceNavigate}
                  />
                );
              } else if (part.value) {
                result.push(part.value);
              }
            });
          }
        } else {
          result.push(child);
        }
      });
      return result;
    },
    [allAssets, onReferenceNavigate]
  );

  // Markdown component overrides – p and li process their children for inline asset refs
  const markdownComponents = useMemo<React.ComponentProps<typeof ReactMarkdown>["components"]>(
    () => ({
      h1: ({ children }) => (
        <h1 className="text-xl font-bold text-zinc-100 mt-4 mb-2 pb-1 border-b border-zinc-700">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-lg font-bold text-zinc-100 mt-4 mb-1.5">{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-base font-semibold text-zinc-200 mt-3 mb-1">{children}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-sm font-semibold text-zinc-300 mt-2 mb-0.5">{children}</h4>
      ),
      h5: ({ children }) => (
        <h5 className="text-xs font-semibold text-zinc-300 mt-2 mb-0.5 uppercase tracking-wide">
          {children}
        </h5>
      ),
      h6: ({ children }) => (
        <h6 className="text-xs font-semibold text-zinc-400 mt-1 mb-0.5 uppercase tracking-wider">
          {children}
        </h6>
      ),
      p: ({ children }) => (
        <p className="text-zinc-300 my-1.5 leading-relaxed">
          {processChildrenForRefs(children as React.ReactNode)}
        </p>
      ),
      ul: ({ children }) => (
        <ul className="list-disc list-outside ml-5 my-1.5 space-y-0.5 text-zinc-300">{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className="list-decimal list-outside ml-5 my-1.5 space-y-0.5 text-zinc-300">
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className="leading-relaxed">{processChildrenForRefs(children as React.ReactNode)}</li>
      ),
      strong: ({ children }) => <strong className="text-zinc-100 font-semibold">{children}</strong>,
      em: ({ children }) => <em className="text-zinc-300 italic">{children}</em>,
      code: ({ children }) => (
        <code className="text-amber-300 bg-zinc-800 px-1 py-0.5 rounded text-xs font-mono">
          {children}
        </code>
      ),
      pre: ({ children }) => (
        <pre className="bg-zinc-800/80 border border-zinc-700 rounded p-3 my-2 overflow-x-auto text-xs font-mono text-zinc-200">
          {children}
        </pre>
      ),
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-zinc-600 pl-3 my-2 text-zinc-400 italic">
          {children}
        </blockquote>
      ),
      hr: () => <hr className="border-zinc-700 my-3" />,
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-amber-400 hover:underline"
        >
          {children}
        </a>
      ),
    }),
    [processChildrenForRefs]
  );

  // Render view mode – full content through a single ReactMarkdown pass so that
  // asset-reference chips appear inline within paragraphs and list items.
  const renderViewContent = () => {
    if (!value) {
      return <span className="text-zinc-600 italic text-sm">No content yet.</span>;
    }
    return (
      <div className="text-sm leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {value}
        </ReactMarkdown>
      </div>
    );
  };

  // Determine what to display:
  // – isControlled  → always show edit textarea (no toggle, no save/cancel)
  // – readOnly      → always show view
  // – otherwise     → toggle between view and edit
  const showEditArea = isControlled || mode === "edit";
  const showToggle = !readOnly && !isControlled;
  const showSaveCancel = !isControlled && mode === "edit" && !!onSave;

  return (
    <div>
      {/* Section header row */}
      {(label || showToggle) && (
        <div className="flex items-center justify-between mb-2">
          {label && (
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">
              {label}
            </h3>
          )}
          {showToggle && (
            <div className="flex gap-1 ml-auto">
              <button
                type="button"
                onClick={() => setMode("view")}
                disabled={mode === "view"}
                className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                  mode === "view"
                    ? "bg-amber-400/15 border-amber-400/40 text-amber-300"
                    : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                }`}
              >
                ● View
              </button>
              <button
                type="button"
                onClick={handleEnterEdit}
                disabled={mode === "edit"}
                className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                  mode === "edit"
                    ? "bg-amber-400/15 border-amber-400/40 text-amber-300"
                    : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                }`}
              >
                ○ Edit
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content area */}
      {!showEditArea ? (
        renderViewContent()
      ) : (
        <div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            rows={6}
            placeholder="Enter Markdown. Type [[ to reference an asset."
            className="w-full bg-zinc-800/60 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-400/50 resize-y font-mono"
          />

          {/* Asset autocomplete dropdown — rendered fixed to escape overflow-hidden parents */}
          {autocompleteQuery !== null && autocompleteAssets.length > 0 && dropdownPos && (
            <div
              ref={autocompleteRef}
              role="listbox"
              style={{
                position: "fixed",
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: 9999,
              }}
              className="bg-zinc-800 border border-zinc-700 rounded-md shadow-2xl max-h-52 overflow-y-auto"
            >
              {autocompleteAssets.map((asset, idx) => {
                const cfg = getTypeConfig(asset.type);
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={asset.id}
                    role="option"
                    aria-selected={isActive}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertReference(asset);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                      isActive ? "bg-zinc-700 text-white" : "hover:bg-zinc-700/60 text-zinc-200"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={cfg.icon}
                      className={`text-[10px] flex-shrink-0 ${cfg.color}`}
                    />
                    <span className="font-medium truncate">{asset.name}</span>
                    {asset.category && (
                      <span className="text-zinc-500 ml-auto flex-shrink-0">{asset.category}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {showSaveCancel && (
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 rounded font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 rounded hover:text-zinc-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetMarkdownField;
